/**
 * searchThreadCorpus — the ONE search rule over what the browser holds
 * (chat-thread experience PR-5, 2026-09-09; the search that suggests, PR-C):
 * thread names (current AND former — a thread found by the name it used to
 * have still opens), routine names, the names of files inside messages, and
 * the text of messages the browser has. Pure: the search box feeds it the
 * corpus; the deep tier (functions/src/prest0nSession/threadSearch.ts) scores
 * the server's copy of the MESSAGES with the same scoring (threadSearchCore,
 * parity-pinned).
 *
 * Names match through matchText (services/search/matchText.ts): every query
 * word, in ANY order, exact > prefix > word start > anywhere, with a bounded
 * typo rescue ("invocies" finds "Invoices"); a former name scores a step
 * lower than the current one; a recency boost lifts what was active within a
 * day (+20) or a week (+10). Messages keep the cross-thread tokenizer +
 * idf-ish scorer + snippet window, so Prest0n's own recall tool, the box and
 * the server agree on what "matches"; at most a few hits per thread, and
 * every hit carries the spans to highlight.
 */
import { extractSnippet, scoreDocument, tokenize } from '../crossThreadSearch/textCore';
import { matchText, queryWords, rangesIn, type MatchRange } from '../search/matchText';

export type { MatchRange } from '../search/matchText';

export interface SearchableThread {
  id: string;
  /** The name the list shows (the display-title rule's output). */
  title: string;
  /** Former names (rename history), any order. */
  formerTitles?: readonly string[];
  archived?: boolean;
  /** ISO — recency tie-break and boost. */
  lastActiveAt?: string;
  pinned?: boolean;
}

export interface SearchableMessage {
  threadId: string;
  id: string;
  /** The shared-store id the row can be scrolled to (see scrollToSharedMessage). */
  sharedMessageId?: string | null;
  text: string;
  isUser?: boolean;
  /** ISO. */
  timestamp?: string;
  senderId?: string;
  /** Who wrote it, for the result row: 'you', 'Prest0n', or a person's name. */
  who?: string;
  /** Names of the files the message carries (the delivered-file lane). */
  attachmentNames?: readonly string[];
}

export interface SearchableRoutine {
  id: string;
  threadId: string;
  name: string;
  /** ISO, or null for a trigger routine. */
  nextRunAt?: string | null;
}

export type ThreadSearchHitKind = 'thread' | 'former-title' | 'message' | 'file' | 'routine';

export interface ThreadSearchHit {
  kind: ThreadSearchHitKind;
  threadId: string;
  /** The thread's CURRENT display title (a routine hit: the routine's name). */
  title: string;
  /** What matched — the name, the former name, the file name, or the message window. */
  snippet: string;
  score: number;
  messageId?: string;
  sharedMessageId?: string;
  timestamp?: string;
  isUser?: boolean;
  who?: string;
  routineId?: string;
  /** Spans of `snippet` (`title` for a 'thread' hit) to highlight. */
  ranges?: MatchRange[];
}

export interface ThreadSearchCorpus {
  threads: readonly SearchableThread[];
  messages?: readonly SearchableMessage[];
  routines?: readonly SearchableRoutine[];
}

export interface SearchThreadCorpusOptions {
  maxThreadHits?: number;
  maxMessageHits?: number;
  maxFileHits?: number;
  maxRoutineHits?: number;
  /** At most this many message hits from ONE thread (the rest are reachable by opening it). */
  maxMessageHitsPerThread?: number;
  maxSnippetChars?: number;
  /** The clock for the recency boost (tests pin it). */
  now?: number;
}

export interface ThreadSearchResults {
  threads: ThreadSearchHit[];
  messages: ThreadSearchHit[];
  files: ThreadSearchHit[];
  routines: ThreadSearchHit[];
}

export const THREAD_SEARCH_CAPS = Object.freeze({ threads: 8, messages: 10, files: 5, routines: 4, perThread: 3 });

export function emptyThreadSearchResults(): ThreadSearchResults {
  return { threads: [], messages: [], files: [], routines: [] };
}

export function isEmptyThreadSearchResults(r: ThreadSearchResults | null | undefined): boolean {
  return !r || (r.threads.length === 0 && r.messages.length === 0 && r.files.length === 0 && r.routines.length === 0);
}

const STOPWORDS = new Set([
  'the', 'and', 'for', 'that', 'this', 'with', 'was', 'were', 'are', 'have', 'has', 'had', 'you', 'your',
  'que', 'con', 'los', 'las', 'del', 'para', 'por', 'como', 'una', 'uno',
]);

/** Lowercase, accent-folded, single-spaced. */
export function foldSearchText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** 0 = no match; 100 exact, 80 prefix, 60 a word starts with it, 40 anywhere (the server's title rule). */
export function titleMatchScore(title: unknown, foldedQuery: string): number {
  const folded = foldSearchText(title);
  if (!folded || !foldedQuery) return 0;
  if (folded === foldedQuery) return 100;
  if (folded.startsWith(foldedQuery)) return 80;
  const idx = folded.indexOf(foldedQuery);
  if (idx === -1) return 0;
  return folded[idx - 1] === ' ' ? 60 : 40;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** +20 for something active within a day, +10 within a week, else 0. */
export function recencyBoost(iso: string | undefined | null, now: number): number {
  if (!iso) return 0;
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return 0;
  const age = now - ms;
  if (age < 0) return 0;
  if (age <= DAY_MS) return 20;
  if (age <= 7 * DAY_MS) return 10;
  return 0;
}

const isStopword = (t: string): boolean => STOPWORDS.has(t);
const byScoreThenNewest = (a: ThreadSearchHit, b: ThreadSearchHit): number =>
  b.score - a.score || (b.timestamp || '').localeCompare(a.timestamp || '');

export function searchThreadCorpus(
  rawQuery: string,
  corpus: ThreadSearchCorpus,
  options: SearchThreadCorpusOptions = {},
): ThreadSearchResults {
  const foldedQuery = foldSearchText(rawQuery);
  const maxThreadHits = options.maxThreadHits ?? THREAD_SEARCH_CAPS.threads;
  const maxMessageHits = options.maxMessageHits ?? THREAD_SEARCH_CAPS.messages;
  const maxFileHits = options.maxFileHits ?? THREAD_SEARCH_CAPS.files;
  const maxRoutineHits = options.maxRoutineHits ?? THREAD_SEARCH_CAPS.routines;
  const perThread = options.maxMessageHitsPerThread ?? THREAD_SEARCH_CAPS.perThread;
  const maxSnippetChars = options.maxSnippetChars ?? 160;
  const now = options.now ?? Date.now();
  const out = emptyThreadSearchResults();
  if (!foldedQuery) return out;

  const titleById = new Map<string, string>();
  for (const t of corpus.threads ?? []) if (t?.id) titleById.set(t.id, t.title);

  // ── Names: current first; a former name a step lower; recency lifts ──
  for (const t of corpus.threads ?? []) {
    if (!t || !t.id) continue;
    const boost = recencyBoost(t.lastActiveAt, now);
    const current = matchText(rawQuery, t.title);
    let best: ThreadSearchHit | null = current
      ? { kind: 'thread', threadId: t.id, title: t.title, snippet: t.title, score: current.score + boost, timestamp: t.lastActiveAt, ranges: current.ranges }
      : null;
    for (const former of t.formerTitles ?? []) {
      const m = matchText(rawQuery, former);
      if (!m) continue;
      const s = m.score * 0.8 + boost;
      if (!best || s > best.score) {
        best = { kind: 'former-title', threadId: t.id, title: t.title, snippet: former, score: s, timestamp: t.lastActiveAt, ranges: m.ranges };
      }
    }
    if (best) out.threads.push(best);
  }
  out.threads.sort(byScoreThenNewest);
  out.threads = out.threads.slice(0, maxThreadHits);

  // ── Routines: by name, soonest next run first among equals ───────────
  for (const r of corpus.routines ?? []) {
    if (!r || !r.id || !r.threadId) continue;
    const m = matchText(rawQuery, r.name);
    if (!m) continue;
    out.routines.push({
      kind: 'routine', threadId: r.threadId, title: r.name, snippet: r.name, score: m.score, routineId: r.id, ranges: m.ranges,
      ...(r.nextRunAt ? { timestamp: r.nextRunAt } : {}),
    });
  }
  out.routines.sort((a, b) => b.score - a.score || (a.timestamp || '9').localeCompare(b.timestamp || '9'));
  out.routines = out.routines.slice(0, maxRoutineHits);

  // ── Files: a message's attachment names ──────────────────────────────
  const messages = corpus.messages ?? [];
  for (const m of messages) {
    if (!m?.threadId || !m.attachmentNames?.length) continue;
    for (const name of m.attachmentNames) {
      const hit = matchText(rawQuery, name);
      if (!hit) continue;
      out.files.push({
        kind: 'file',
        threadId: m.threadId,
        title: titleById.get(m.threadId) ?? '',
        snippet: name,
        score: hit.score + recencyBoost(m.timestamp, now),
        messageId: m.id,
        ranges: hit.ranges,
        ...(m.sharedMessageId ? { sharedMessageId: m.sharedMessageId } : {}),
        ...(m.timestamp ? { timestamp: m.timestamp } : {}),
        ...(m.who ? { who: m.who } : {}),
      });
    }
  }
  out.files.sort(byScoreThenNewest);
  out.files = out.files.slice(0, maxFileHits);

  // ── Messages: the cross-thread scorer over the browser-held text ──────
  if (messages.length > 0) {
    const queryTokens = tokenize(rawQuery).filter((t) => !isStopword(t));
    const hits: ThreadSearchHit[] = [];
    const messageHit = (m: SearchableMessage, snippet: string, score: number, highlightWords: readonly string[]): ThreadSearchHit => ({
      kind: 'message',
      threadId: m.threadId,
      title: titleById.get(m.threadId) ?? '',
      snippet,
      score,
      messageId: m.id,
      ranges: rangesIn(snippet, highlightWords),
      ...(m.sharedMessageId ? { sharedMessageId: m.sharedMessageId } : {}),
      ...(m.timestamp ? { timestamp: m.timestamp } : {}),
      ...(m.isUser !== undefined ? { isUser: m.isUser } : {}),
      ...(m.who ? { who: m.who } : {}),
    });
    if (queryTokens.length > 0) {
      const docs: Array<{ m: SearchableMessage; tokens: string[] }> = [];
      const df = new Map<string, number>();
      for (const m of messages) {
        const text = typeof m?.text === 'string' ? m.text : '';
        if (!m?.threadId || text.trim().length < 2) continue;
        const tokens = tokenize(text).filter((t) => !isStopword(t));
        if (tokens.length === 0) continue;
        for (const tok of new Set(tokens)) df.set(tok, (df.get(tok) || 0) + 1);
        docs.push({ m, tokens });
      }
      for (const { m, tokens } of docs) {
        let score = scoreDocument(queryTokens, tokens, df);
        // A verbatim phrase in the text is a stronger signal than token overlap.
        if (foldSearchText(m.text).includes(foldedQuery)) score += 1;
        if (score <= 0) continue;
        hits.push(messageHit(m, extractSnippet(m.text, queryTokens, maxSnippetChars), score, [...queryTokens, foldedQuery]));
      }
    } else {
      // Only stopwords / very short — fall back to a verbatim window.
      for (const m of messages) {
        const text = typeof m?.text === 'string' ? m.text : '';
        if (!m?.threadId || !foldSearchText(text).includes(foldedQuery)) continue;
        hits.push(messageHit(m, extractSnippet(text, [foldedQuery], maxSnippetChars), 0.5, [foldedQuery]));
      }
    }
    hits.sort(byScoreThenNewest);
    const perThreadCount = new Map<string, number>();
    for (const h of hits) {
      const n = perThreadCount.get(h.threadId) ?? 0;
      if (n >= perThread) continue;
      perThreadCount.set(h.threadId, n + 1);
      out.messages.push(h);
      if (out.messages.length >= maxMessageHits) break;
    }
  }

  return out;
}

/** The query's folded words — what the UI highlights inside a deep hit's snippet. */
export function highlightWordsFor(rawQuery: string): string[] {
  const words = queryWords(rawQuery);
  const folded = foldSearchText(rawQuery);
  return folded && !words.includes(folded) ? [...words, folded] : words;
}
